import React, {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import useHoverDelay from 'Components/hooks/use-hover-delay'
import Portal from 'Components/portal'
import classNames from 'classnames'
import { usePopper } from 'react-popper'
import { Placement } from '@popperjs/core/lib/enums'
import styleModule from './index.module.scss'

export type TOffset = (number | null | undefined)[]
export interface IProps extends React.AnchorHTMLAttributes<HTMLElement> {
  children?: ReactNode | ((showTips: boolean) => ReactNode)
  content?:
    | ReactNode
    | ((params: {
        open(): void
        close(): void
        showTips: boolean
      }) => ReactNode)
  theme?: 'dark' | 'light'
  onTipShow?(): void
  onTipClose?(): void
  onClick?(event: React.MouseEvent): void
  className?: string
  wrapperClassName?: string
  style?: CSSProperties
  popperStyle?: CSSProperties
  tag?: string
  placement?: Placement
  arrowVisible?: boolean
  renderSSR?: boolean
  gap?: number // 下拉框与内容的间距
  offset?: TOffset // 下拉框与内容的偏移，优先以此配置为准，没有时设置为[0, gap]
  trigger?: 'hover' | 'click' | 'focus'
  getContainer?: () => HTMLElement
  beforeClose?(): boolean
  disabled?: boolean
  transfer?: boolean
}

const ToolTip = ({
  children,
  content,
  theme = 'dark',
  onTipShow,
  onTipClose,
  onClick,
  className,
  wrapperClassName,
  style,
  tag = 'span',
  placement = 'top',
  arrowVisible = true,
  renderSSR,
  gap = 12,
  offset,
  trigger = 'hover',
  getContainer,
  disabled = false,
  transfer = true,
  popperStyle,
  ...rest
}: IProps) => {
  const Tag: any = tag
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  )
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null)
  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      modifiers: [
        {
          name: 'arrow',
          options: { element: arrowElement },
        },
        {
          name: 'offset',
          options: {
            offset: offset ? (offset as any) : [0, gap],
          },
        },
      ],
      placement,
    }
  )

  const isEnterRef = useRef(false)
  const [isInit, setIsInit] = useState(renderSSR)
  const [showTips, setShowTips] = useState(false)
  const [isLeave, setIsLeave] = useState(false)

  const enter = useCallback(() => {
    if (showTips) {
      return
    }
    if (update) {
      update()
    }
    setIsInit(true)
    setShowTips(true)
    setIsLeave(false)

    if (onTipShow) {
      onTipShow()
    }
  }, [onTipShow, isInit, update, showTips])

  const leave = useCallback(() => {
    if (isEnterRef.current) {
      return
    }
    setIsLeave(true)

    if (onTipClose) onTipClose()
  }, [onTipClose])

  const [startShow, cancelShow] = useHoverDelay(enter)
  const [startClose, cancelClose] = useHoverDelay(leave)

  const handleEnter = useCallback(() => {
    if (disabled) return
    isEnterRef.current = true
    startShow(1)
    cancelClose()
  }, [startShow, cancelClose, disabled])

  const handleClick = () => {
    if (disabled) return
    isEnterRef.current = true
    enter()
    cancelClose()
  }

  const handleLeave = useCallback(() => {
    isEnterRef.current = false
    cancelShow()
    startClose(1)
  }, [cancelShow, startClose])

  const handleAnimationEnd = useCallback(() => {
    if (isLeave) {
      setShowTips(false)
      setIsLeave(false)
    }
  }, [isLeave])

  const curContent = useMemo(() => {
    return typeof content === 'function'
      ? content({
          open: enter,
          showTips,
          close: () => {
            isEnterRef.current = false
            leave()
          },
        })
      : content
  }, [content, enter, leave, showTips])

  return (
    <Tag
      ref={setReferenceElement}
      onMouseOver={trigger === 'hover' ? handleEnter : undefined}
      onClick={(e: React.MouseEvent) => {
        if (disabled) return
        if (trigger === 'click') {
          e.stopPropagation()
          handleClick()
        }
        onClick?.(e)
      }}
      onFocus={trigger === 'focus' ? handleEnter : undefined}
      onMouseOut={trigger === 'focus' ? undefined : handleLeave}
      onBlur={trigger === 'focus' ? handleLeave : undefined}
      className={classNames('tw-relative', className)}
      style={style}
      {...rest}
    >
      {useMemo(
        () => (typeof children === 'function' ? children(showTips) : children),
        [showTips, children]
      )}
      {showTips && (
        <div className="tw-absolute tw-left-0 tw-right-0 tw-h-40 tw--bottom-40" />
      )}
      {isInit && curContent ? (
        <Portal
          getContainer={getContainer || (() => document.body)}
          renderSSR={renderSSR}
          transfer={transfer}
        >
          <div
            ref={setPopperElement}
            {...attributes.popper}
            className={classNames(wrapperClassName, styleModule['wrapper'])}
            style={{
              display: showTips ? 'block' : 'none',
              zIndex: 10200,
              ...popperStyle,
              ...styles.popper,
            }}
            onClick={(e) => {
              if (trigger === 'click') e.stopPropagation()
            }}
            onMouseOver={trigger === 'click' ? handleEnter : undefined}
          >
            <div
              className={classNames(styleModule['content'], theme, {
                enter: !isLeave,
                leave: isLeave,
              })}
              onAnimationEnd={handleAnimationEnd}
            >
              {curContent}
              <div
                ref={setArrowElement}
                style={{
                  display: arrowVisible ? '' : 'none',
                  ...styles.arrow,
                }}
                className={classNames(styleModule['arrow'], theme)}
              />
            </div>
          </div>
        </Portal>
      ) : null}
    </Tag>
  )
}

export default ToolTip
